import { useState } from 'react';

import cn from 'classnames/bind';
import PropTypes from 'prop-types';
import {
  Accordion, ArrowUp, Input, Label, Text,
} from 'ui';
import OptionsAnswer from 'ui/components/tests/OptionsAnswer';
import OrderAnswer from 'ui/components/tests/OrderAnswer';
import RangeAnswer from 'ui/components/tests/RangeAnswer';
import TestWrapper from 'ui/components/tests/TestWrapper';

import RadioGroupAnswer from '../RadioGroupAnswer/RadioGroupAnswer';

import s from './Question.module.scss';

const cx = cn.bind(s);

const Question = ({
  question,
  description,
  descriptionText,
  isStacked,
  isLastStackedQuestion,
  stackTitle,
  className,
  value,
  name,
  reverse,
  optional = false,
  agreeLabel = 'Agree',
  disagreeLabel = 'Disagree',
  type = 'range',
  header = null,
  singleType = null,
  options = [],
  answerExtraProps = {},
  hasBorder = true,
  onChange,
  color,
  schema,
  content,
  CustomComponent = null,
  ...attrs
}) => {
  const [progress, setProgress] = useState(0);
  const handleChange = (eventOrValue) => {
    const isEvent = typeof eventOrValue === 'object' && !!eventOrValue.target;
    const newValue = isEvent ? eventOrValue.target.value || '' : eventOrValue;
    if (onChange) {
      onChange({
        name,
        value: newValue,
        oldValue: value,
        questionType: type,
      });
    }
  };
  const handleTestQuestionChange = ({ answers, progress: testProgress }) => {
    handleChange(answers);
    setProgress(testProgress);
  };
  if (type === 'text') {
    return content;
  }
  if (type === 'custom') {
    return <CustomComponent value={value} handleChange={handleChange} {...options}/>
  }
  return (
    <div
      className={cx(className, 'questionWrapper', color, {
        hasBorder,
        questionWrapperStacked: isStacked,
        questionWrapperLastStacked: isLastStackedQuestion,
      })}
      {...attrs}
    >
      {
        type !== 'text' && (<>
          {header && (
            <Text center as="h3" className={cx('questionHeader')}>{header}</Text>
          )}
          {stackTitle && (
            <Text as="h3" className={cx('question')}>{stackTitle}</Text>
          )}
          <div
            className={cx('question', {
              stackedQuestion: isStacked,
              withDescription: description,
              'tp-mobile-header-4 md:tp-header-4': isStacked,
              'tp-mobile-header-3 md:tp-header-3': !isStacked,
            })}
          >
            {question}{optional ? <span className={cx('notice')}> (optional)</span> : ''}
          </div>
          {description && (
            <Text as="h4" tag="div" className={cx('description')}>
              ({description})
            </Text>
          )}
          {descriptionText && (
            <div className={cx('descriptionText', 'tp-text', 'tp-text')}>
              {descriptionText}
            </div>
          )}
        </>)
      }
      <div className={cx('answers')}>
        {type === 'range' && (
          <RangeAnswer
            agreeLabel={agreeLabel}
            disagreeLabel={disagreeLabel}
            singleType={singleType}
            value={value}
            reverse={reverse}
            onChange={handleChange}
          />
        )}
        {type === 'options' && (
          <OptionsAnswer options={options} value={value} onChange={handleChange} />
        )}
        {type === 'multipleOptions' && (
          <OptionsAnswer
            options={options}
            color={color}
            value={value}
            multiple
            {...answerExtraProps}
            onChange={handleChange}
          />
        )}
        {type === 'string' && <Input value={value} {...answerExtraProps} onChange={handleChange} />}
        {type === 'order' && (
          <OrderAnswer
            value={value}
            color={color}
            options={options}
            {...answerExtraProps}
            onChange={handleChange}
          />
        )}
        {type === 'boolean' && (
          <OptionsAnswer
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
            value={value}
            onChange={handleChange}
          />
        )}
        {type === 'radiogroup' && (
          <div className={cx('radiogroup')}>
            <RadioGroupAnswer
              options={options}
              value={value}
              onChange={handleChange}
            />
          </div>
        )}
        {type === 'test' && (
          <Accordion>
            <Accordion.Header className={cx('testQuestionLabelWrapper')}>
              {(isOpen) => (
                <div className={cx('testQuestionLabel', { testQuestionLabelOpened: isOpen })}>
                  <Text variant="h4">{question}</Text>
                  <span className={cx('testQuestionLabelRight')}>
                    <span className={cx('testQuestionLabelStatus')}>
                      {progress === 100 && <Label>Completed</Label>}
                    </span>
                    <span className={cx('testQuestionLabelIcon')}>
                      <ArrowUp className={cx('arrowIcon')} isOpen={isOpen} />
                    </span>
                  </span>
                </div>
              )}
            </Accordion.Header>
            <Accordion.Content>
              <div className={cx('testQuestionContent')}>
                <TestWrapper
                  schema={schema}
                  schemaName={question}
                  hasSimpleLayout
                  onChange={handleTestQuestionChange}
                />
              </div>
            </Accordion.Content>
          </Accordion>
        )}
      </div>
    </div>
  );
};

Question.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default Question;
